import React, { useEffect, useState } from 'react';
import {
    Table, TableBody, TableRow, TableCell, TableContainer, Pagination, Typography, Box, Chip, IconButton, Tooltip
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ModeOutlinedIcon from '@mui/icons-material/ModeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import Scrollbar from '../../../Scrollbar';
import CutoffLunchMenuTableHead from './cutoffLunchMenuTableHeader';
import DeleteDialog from '../../../../pages/admin/kitchen/deleteDialog';
import { to12HourTime, getTodayInputDate } from '../../../../utils/common';
import {
    getCutoffScheduleWatcher, toggleCutoffScheduleWatcher, generateCutoffScheduleWatcher, deleteCutoffScheduleWatcher
} from '../../../../redux/actionCreators/admin/cutoffLunchMenu';
import { scheduleSummary, statusOf, formatDate } from './scheduleUtils';

const TABLE_HEAD = [
    { id: 'nID', label: 'ID', alignRight: false },
    { id: 'Summary', label: 'Schedule', alignRight: false },
    { id: 'StartTime', label: 'Time', alignRight: false },
    { id: 'LunchCount', label: 'Count / Day', alignRight: false },
    { id: 'UserTypeName', label: 'Type', alignRight: false },
    { id: 'VendorName', label: 'Vendor', alignRight: false },
    { id: 'MenuItemName', label: 'Menu Item', alignRight: false },
    { id: 'Status', label: 'Status', alignRight: false },
    { id: 'NextRunDate', label: 'Next Run', alignRight: false },
];

const STATUS_COLOR = { Active: 'success', Paused: 'warning', Ended: 'default' }

const compare = (a, b, orderBy) => (b[orderBy] < a[orderBy] ? -1 : b[orderBy] > a[orderBy] ? 1 : 0)

const ScheduleRow = ({ row, onEdit }) => {
    const dispatch = useDispatch()
    const [deleteOpen, setDeleteOpen] = useState(false)

    const ended = row.Status === 'Ended'
    // "HH:mm" strings compare correctly as text
    const windowOver = !!row.EndTime && row.EndTime < new Date().toTimeString().slice(0, 5)

    const generateHint = row.GeneratedToday
        ? "Today's cutoff is already created"
        : windowOver ? "Today's window has already ended" : "Create today's cutoff now"

    return (
        <TableRow hover tabIndex={-1}>
            <TableCell><Typography variant='t14_600'>{row.nID}</Typography></TableCell>
            <TableCell sx={{ maxWidth: 220 }}><Typography variant='t14_600'>{row.Summary}</Typography></TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant='t14_600'>{to12HourTime(row.StartTime)} - {to12HourTime(row.EndTime)}</Typography>
            </TableCell>
            <TableCell><Typography variant='t14_600'>{row.LunchCount}</Typography></TableCell>
            <TableCell><Typography variant='t14_600'>{row.UserTypeName}</Typography></TableCell>
            <TableCell><Typography variant='t14_600'>{row.VendorName}</Typography></TableCell>
            <TableCell><Typography variant='t14_600'>{row.MenuItemName}</Typography></TableCell>
            <TableCell>
                <Chip size='small' label={row.Status} color={STATUS_COLOR[row.Status]} variant='outlined' />
                {row.GeneratedToday ? <Chip size='small' label='Live today' color='primary' sx={{ ml: 0.5 }} /> : null}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant='t14_600'>{row.AutoGenerate ? (row.NextRunDate ? formatDate(row.NextRunDate) : '-') : 'Manual'}</Typography>
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title={generateHint}>
                    <span>
                        <IconButton disabled={!!row.GeneratedToday || !!windowOver}
                            onClick={() => dispatch(generateCutoffScheduleWatcher(row.nID))}>
                            <EventAvailableIcon sx={{ color: row.GeneratedToday || windowOver ? 'inherit' : 'common.black' }} />
                        </IconButton>
                    </span>
                </Tooltip>
                {row.AutoGenerate && !ended ?
                    <Tooltip title={row.bIsActive ? 'Pause: stop creating cutoffs' : 'Resume'}>
                        <IconButton onClick={() => dispatch(toggleCutoffScheduleWatcher(row.nID, !row.bIsActive))}>
                            {row.bIsActive
                                ? <PauseCircleOutlineIcon sx={{ color: 'common.black' }} />
                                : <PlayCircleOutlineIcon sx={{ color: 'common.black' }} />}
                        </IconButton>
                    </Tooltip> : null}
                <Tooltip title='Edit (future days only)'>
                    <IconButton onClick={() => onEdit(row)}>
                        <ModeOutlinedIcon sx={{ color: 'common.black' }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete (cutoffs it already created stay)">
                    <IconButton onClick={() => setDeleteOpen(true)}>
                        <DeleteOutlineIcon sx={{ color: 'common.black' }} />
                    </IconButton>
                </Tooltip>
                <DeleteDialog open={deleteOpen} setOpen={setDeleteOpen} type='cutoffSchedule'
                    handleDelete={() => { dispatch(deleteCutoffScheduleWatcher(row.nID)); setDeleteOpen(false) }} />
            </TableCell>
        </TableRow>
    )
}

const CutoffScheduleTable = ({ onEdit }) => {
    const dispatch = useDispatch();
    const { CutoffLunchMenuReducers: { cutoffScheduleList } } = useSelector((state) => state)

    const [page, setPage] = useState(1);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('nID');
    const rowsPerPage = 5;

    useEffect(() => {
        dispatch(getCutoffScheduleWatcher())
    }, [dispatch])

    const today = getTodayInputDate()

    const rows = (cutoffScheduleList || []).map((r) => ({ ...r, Summary: scheduleSummary(r), Status: statusOf(r, today) }))
    const sorted = rows
        .map((el, i) => [el, i])
        .sort((a, b) => (order === 'desc' ? compare(a[0], b[0], orderBy) : -compare(a[0], b[0], orderBy)) || a[1] - b[1])
        .map((el) => el[0])

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    return (
        <>
            <Box>
                <Scrollbar>
                    <TableContainer sx={{ minWidth: 600, overflow: 'unset' }}>
                        <Table>
                            <CutoffLunchMenuTableHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleRequestSort} />
                            <TableBody>
                                {sorted.slice(rowsPerPage * (page - 1), rowsPerPage * page).map((r) => (
                                    <ScheduleRow key={`${r.nID}-${r.VendorId}`} row={r} onEdit={onEdit} />
                                ))}
                                {!sorted.length &&
                                    <TableRow>
                                        <TableCell align="center" colSpan={TABLE_HEAD.length + 1} sx={{ py: 3 }}>
                                            <Typography variant='t18_500'>No schedules yet</Typography>
                                        </TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Scrollbar>
            </Box>
            <Box display='flex' justifyContent='right' paddingTop='20px'>
                <Pagination shape="rounded" color='primary' count={Math.ceil(sorted.length / rowsPerPage)} page={page} onChange={(e, p) => setPage(p)} />
            </Box>
        </>
    )
}

export default CutoffScheduleTable;
